import {
  getBusinessTodayDate,
  parseISOToLocalDate,
} from '../../utils/dateUtils';

/**
 * How close a license is to lapsing.
 *
 * `null` means nothing worth flagging: either it has no expiration date at all
 * (a W9 or a Social Security Card on file never expires) or it expires far
 * enough out that a badge would be noise on every row.
 */
export type LicenseExpiryStatus = 'expired' | 'expiring' | null;

/**
 * Days before expiry that a license starts being called out.
 *
 * Deliberately a constant here rather than a setting: this is the badge on the
 * member's own record, and the reports ticket is free to choose its own
 * threshold without the two having to agree.
 */
export const EXPIRING_SOON_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Classify one expiration date.
 *
 * `today` is injectable so a test can pin it; it defaults to the business date
 * in Pacific Time, matching how the rest of the product decides what "today"
 * is regardless of where the browser sits.
 */
export const getLicenseExpiryStatus = (
  expirationDate: string | null | undefined,
  today: Date = getBusinessTodayDate()
): LicenseExpiryStatus => {
  const expiry = parseISOToLocalDate(expirationDate);
  if (!expiry) return null;

  // Compare whole days, not instants: a license expiring today has not lapsed
  // yet, and an hours-apart comparison would say otherwise depending on when
  // the page was opened.
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const daysRemaining = Math.round(
    (expiry.getTime() - startOfToday.getTime()) / MS_PER_DAY
  );

  if (daysRemaining < 0) return 'expired';
  if (daysRemaining <= EXPIRING_SOON_DAYS) return 'expiring';
  return null;
};

export const LICENSE_EXPIRY_LABELS: Record<
  Exclude<LicenseExpiryStatus, null>,
  string
> = {
  expired: 'Expired',
  expiring: 'Expires soon',
};
